import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Volume2, Bot, Brain, Mic, FileText, Link, Upload, Settings, Globe, Phone, Loader2, Plus, Trash2, Play, Pause, Search, ChevronRight, ChevronLeft, Check, X, Wrench, Copy, CheckCircle, XCircle, RefreshCw, Zap, Info, ChevronDown, ChevronUp, Eye, Pencil, Sparkles, Shield, Target, MessageSquare, AlertTriangle, Type, GitBranch, type LucideIcon } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { Card, CardContent } from '@easyflow/ui-frontend/components/ui/card';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Label } from '@easyflow/ui-frontend/components/ui/label';
import { Textarea } from '@easyflow/ui-frontend/components/ui/textarea';
import { Switch } from '@easyflow/ui-frontend/components/ui/switch';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@easyflow/ui-frontend/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@easyflow/ui-frontend/components/ui/tooltip';
import { useToast } from '@/hooks/use-toast';
import { apiClient, type ApiResponse } from '@/lib/api';
import { isTenantLimitHandled } from '@/lib/tenantLimits';
import { fetchChatModelOptions, chatModelOptionsQueryKey, type ChatModelEntry as LLMModelEntry } from '@/hooks/useChatModelOptions';
import { PROVIDER_METADATA, type LLMProvider } from '@/constants/ai-providers';
import { AgentConfigShell, type AgentSectionDef } from '@/components/ai/shell/AgentConfigShell';
import { KnowledgeBaseManager } from '@/components/ai/KnowledgeBaseManager';
import VoiceAgentFlowBuilder, { type FlowDefinition } from '@/components/ai/VoiceAgentFlowBuilder';
import PreCallHooksSection, { normalizePreCallHooks, type PreCallHooksConfig } from '@/components/ai/PreCallHooksSection';
import { CustomLLMPicker } from '@/components/ai/CustomLLMPicker';
import {
  KbModelChangeGuardDialog,
  useKbModelChangeGuard,
  type KbGuardedPerformSaveArgs,
} from '@/components/ai/KbModelChangeGuardDialog';

// metadata jsonb dinamico: tipizziamo solo i campi che il FE legge/scrive,
// il resto resta libero come richiede il payload backend.
export interface ElevenLabsAgentMetadata {
  approval_mode?: string;
  dynamic_context_enabled?: boolean;
  [key: string]: unknown;
}

// Config provider AI (claude/gemini): il FE legge solo hint + modello default.
interface AiToolProviderConfig {
  api_key_hint?: string;
  default_model?: string;
  [key: string]: unknown;
}

// L'endpoint può rispondere sia con envelope singolo (res.data) sia con
// envelope doppio (res.data.data). Estraiamo in modo difensivo la config.
function extractProviderConfig(res: ApiResponse<AiToolProviderConfig>): AiToolProviderConfig | undefined {
  const outer = res?.data as AiToolProviderConfig & { data?: AiToolProviderConfig } | undefined;
  return outer?.data || outer;
}

export interface ElevenLabsAgentItem {
  id: string;
  elevenlabs_agent_id: string;
  name: string;
  description?: string;
  voice_id?: string;
  voice_name?: string;
  llm_provider: string;
  llm_model: string;
  custom_llm_url?: string;
  custom_endpoint_id?: string | null;
  custom_model_id?: string | null;
  prompt?: string;
  first_message?: string;
  language: string;
  tags: string[];
  max_duration_seconds: number;
  channel_sip?: boolean;
  channel_webrtc?: boolean;
  sip_phone_number?: string;
  webrtc_widget_config?: Record<string, unknown>;
  voice_stability: number;
  voice_similarity: number;
  tts_model?: string;
  temperature: number;
  max_tokens: number;
  is_active: boolean;
  metadata: ElevenLabsAgentMetadata;
  conversation_count?: number;
  linked_ai_agent_id?: string;
  dynamic_context_enabled?: boolean;
  flow_definition?: FlowDefinition | null;
  // payload jsonb grezzo dal backend: normalizzato via normalizePreCallHooks(input: unknown).
  pre_call_hooks?: unknown;
  created_at: string;
  transcription_enabled?: boolean;
  sentiment_enabled?: boolean;
  context_memory_enabled?: boolean;
  semantic_memory_enabled?: boolean;
  rag_enabled?: boolean;
  rag_inferential_enabled?: boolean;
  rag_synthesis_mode?: string;
  elevenlabs_remote_config?: {
    llm_provider: string;
    llm_model: string;
    tts_model: string;
    tool_ids?: string[];
    tools_count: number;
  };
}

interface ElevenLabsAgentConfigModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  agent?: ElevenLabsAgentItem | null;
  onSaved: () => void;
}

interface VoiceItem {
  voice_id: string;
  name: string;
  category?: string;
  description?: string;
  labels?: Record<string, string>;
  preview_url?: string;
  language?: string;
  gender?: string;
  age?: string;
  accent?: string;
  use_case?: string;
  creator_username?: string;
  public_owner_id?: string;
  _source?: 'account' | 'library';
}


const LANGUAGES = [
  { id: 'it', name: 'Italiano', flag: '🇮🇹' },
  { id: 'en', name: 'English', flag: '🇬🇧' },
  { id: 'fr', name: 'Français', flag: '🇫🇷' },
  { id: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { id: 'es', name: 'Español', flag: '🇪🇸' },
];

interface ElevenLabsProviderTile {
  id: string;
  label: string;
  icon: typeof Brain;
  color: string;
  bg: string;
  border: string;
  desc: string;
}

// Task #1066: LLM provider entries derive labels from PROVIDER_METADATA
// (single source of truth). Only ElevenLabs Native + Custom are local since
// they are not LLM providers.
const LLM_PROVIDER_TILE_STYLES: Record<LLMProvider, { icon: typeof Brain; color: string; bg: string; border: string; desc: string }> = {
  openai: { icon: Brain, color: 'text-blue-500', bg: 'bg-blue-50', border: 'border-blue-300', desc: 'Usa la chiave API OpenAI centralizzata' },
  claude: { icon: Brain, color: 'text-purple-500', bg: 'bg-purple-50', border: 'border-purple-300', desc: 'Usa la chiave API Anthropic centralizzata' },
  gemini: { icon: Brain, color: 'text-green-500', bg: 'bg-green-50', border: 'border-green-300', desc: 'Usa la chiave API Google centralizzata' },
  custom: { icon: Bot, color: 'text-muted-foreground', bg: 'bg-muted', border: 'border-border', desc: 'Endpoint OpenAI-compatible tenant-scoped (Ollama, vLLM, RunPod, LM Studio, ecc.)' },
};

const PROVIDERS: ElevenLabsProviderTile[] = [
  ...(Object.keys(LLM_PROVIDER_TILE_STYLES) as LLMProvider[]).map((id) => ({
    id,
    label: PROVIDER_METADATA[id].label,
    ...LLM_PROVIDER_TILE_STYLES[id],
  })),
  { id: 'elevenlabs', label: 'ElevenLabs Native', icon: Volume2, color: 'text-emerald-500', bg: 'bg-emerald-50', border: 'border-emerald-300', desc: 'Modelli interni ElevenLabs' },
  { id: 'custom', label: 'Custom', icon: Settings, color: 'text-muted-foreground', bg: 'bg-muted', border: 'border-border', desc: 'Qualsiasi endpoint OpenAI-compatible' },
];

const MODELS_BY_PROVIDER: Record<string, { id: string; name: string; desc?: string; badge?: string }[]> = {
  openai: [
    { id: 'gpt-5.4', name: 'GPT-5.4', desc: 'Il più avanzato, tool use nativo e ragionamento superiore', badge: 'Consigliato' },
    { id: 'gpt-5.4-mini', name: 'GPT-5.4 Mini', desc: 'Veloce e economico, instruction following superiore', badge: 'Veloce' },
    { id: 'gpt-4o', name: 'GPT-4o', desc: 'Flagship multimodale (gen. precedente)' },
    { id: 'gpt-4o-mini', name: 'GPT-4o Mini', desc: 'Veloce e economico (gen. precedente)' },
    { id: 'gpt-4.1', name: 'GPT-4.1', desc: 'Migliore su istruzioni complesse' },
    { id: 'gpt-4.1-mini', name: 'GPT-4.1 Mini', desc: 'Versione leggera del 4.1, veloce e preciso' },
    { id: 'o3-mini', name: 'o3 Mini', desc: 'Ragionamento avanzato, ottimo per decisioni complesse', badge: 'Reasoning' },
  ],
  claude: [
    { id: 'claude-sonnet-4-6', name: 'Claude Sonnet 4.6', desc: 'Miglior rapporto velocità/intelligenza', badge: 'Consigliato' },
    { id: 'claude-opus-4-6', name: 'Claude Opus 4.6', desc: 'Il più intelligente, agenti e coding avanzato', badge: 'Premium' },
    { id: 'claude-haiku-4-5', name: 'Claude Haiku 4.5', desc: 'Economico e velocissimo', badge: 'Veloce' },
  ],
  gemini: [
    { id: 'gemini-2.5-flash', name: 'Gemini 2.5 Flash', desc: 'Miglior rapporto qualità/prezzo, thinking integrato, 1M context', badge: 'Consigliato' },
    { id: 'gemini-2.5-pro', name: 'Gemini 2.5 Pro', desc: 'Massima qualità, ragionamento avanzato, 1M context', badge: 'Premium' },
    { id: 'gemini-3-flash-preview', name: 'Gemini 3 Flash Preview', desc: 'Ultima generazione, performance avanzate', badge: 'Preview' },
  ],
};

// Modelli mostrati nella Select (desc/badge dal provider). Solo i campi
// letti dalla UI sono tipizzati.
interface ModelOption {
  id: string;
  name: string;
  desc?: string;
  badge?: string;
}

const STEPS = [
  { label: 'Generale', icon: Settings },
  { label: 'LLM', icon: Brain },
  { label: 'Voce', icon: Mic },
  { label: 'Knowledge', icon: FileText },
  { label: 'Tools', icon: Wrench },
  { label: 'Hooks', icon: Zap },
  { label: 'Canale', icon: Phone },
];

const VOICE_CATEGORIES = [
  { id: 'all', label: 'Tutte' },
  { id: 'premade', label: 'Premade' },
  { id: 'cloned', label: 'Clonate' },
  { id: 'generated', label: 'Generate' },
];

interface PromptSection {
  id: string;
  label: string;
  icon: LucideIcon;
  placeholder: string;
  tooltip: string;
}

const PROMPT_SECTIONS: PromptSection[] = [
  {
    id: 'personality',
    label: 'Personalità',
    icon: Sparkles,
    placeholder: 'Sei un receptionist virtuale per [Azienda]. Sei professionale, cortese e orientato alla soluzione.',
    tooltip: 'Definisci chi è l\'agente: ruolo, carattere e tratti distintivi. Sii conciso e specifico — ogni parola inutile è una potenziale fonte di ambiguità per il modello. Es: "Sei un assistente tecnico paziente e metodico per CloudTech."'
  },
  {
    id: 'environment',
    label: 'Ambiente',
    icon: Globe,
    placeholder: 'Stai assistendo clienti tramite telefono. I clienti potrebbero essere frustrati per problemi di servizio.',
    tooltip: 'Descrivi il contesto operativo: dove opera l\'agente (telefono, chat, widget), tipo di clienti attesi e risorse disponibili. Questo aiuta il modello ad adattare il comportamento al canale.'
  },
  {
    id: 'tone',
    label: 'Tono',
    icon: MessageSquare,
    placeholder: 'Rispondi in modo chiaro e conciso (2-3 frasi). Usa un tono calmo e professionale. Adatta il livello tecnico in base al cliente.',
    tooltip: 'Definisci lo stile comunicativo: lunghezza risposte, livello di formalità, adattamento al contesto. Evita di ripetere indicazioni sul tono in altre sezioni — centralizzale qui per chiarezza.'
  },
  {
    id: 'goal',
    label: 'Obiettivo',
    icon: Target,
    placeholder: '1. Verifica l\'identità del cliente con email e codice cliente\n2. Identifica il problema e la priorità\n3. Esegui la diagnostica con il tool appropriato\n4. Fornisci una soluzione o escalation dopo 2 tentativi',
    tooltip: 'Il workflow step-by-step che l\'agente deve seguire. Usa una lista numerata con un\'azione per riga. Aggiungi "Questo passaggio è importante" dopo i passaggi critici per rafforzarli. Il modello segue più fedelmente istruzioni strutturate.'
  },
  {
    id: 'guardrails',
    label: 'Guardrails',
    icon: Shield,
    placeholder: 'Non accedere mai ai dati del cliente senza verifica identità.\nNon inventare informazioni — basa le risposte solo sui dati disponibili.\nSe il problema persiste dopo 2 tentativi, escalation al team.',
    tooltip: 'Regole NON negoziabili che il modello deve sempre rispettare. I modelli sono addestrati a prestare particolare attenzione alla sezione # Guardrails. Centralizza qui tutte le regole di sicurezza e compliance per facilità di audit.'
  },
  {
    id: 'error_handling',
    label: 'Gestione Errori',
    icon: AlertTriangle,
    placeholder: 'Se un tool fallisce:\n1. Comunica il problema: "Sto avendo difficoltà ad accedere a queste informazioni."\n2. Non inventare dati\n3. Riprova una volta, poi offri escalation a un operatore',
    tooltip: 'Ogni chiamata a tool esterno può fallire. Definisci come reagire a: errori di rete, dati mancanti, timeout, errori di permessi. Senza istruzioni esplicite, l\'agente potrebbe inventare risposte o fornire informazioni errate.'
  },
  {
    id: 'normalization',
    label: 'Normalizzazione',
    icon: Type,
    placeholder: 'Email:\n- Parlato: "mario punto rossi chiocciola azienda punto it"\n- Scritto: "mario.rossi@azienda.it"\n- Converti "chiocciola" → "@", "punto" → "."',
    tooltip: 'Definisci come convertire dati strutturati (email, numeri, codici) dal formato parlato a quello scritto e viceversa. Gli agenti vocali spesso interpretano male simboli come "@" o ".". La normalizzazione garantisce accuratezza nei tool e naturalezza nel parlato.'
  },
];

interface PromptSections {
  personality: string;
  environment: string;
  tone: string;
  goal: string;
  guardrails: string;
  error_handling: string;
  normalization: string;
}

const emptyPromptSections: PromptSections = {
  personality: '',
  environment: '',
  tone: '',
  goal: '',
  guardrails: '',
  error_handling: '',
  normalization: '',
};

interface PromptTemplate {
  id: string;
  label: string;
  description: string;
  sections: PromptSections;
}

const PROMPT_TEMPLATES: PromptTemplate[] = [
  {
    id: 'receptionist',
    label: 'Receptionist Virtuale',
    description: 'Accoglienza e smistamento chiamate',
    sections: {
      personality: 'Sei una receptionist virtuale per [Nome Azienda]. Sei cordiale, professionale e efficiente nello smistamento delle chiamate.',
      environment: 'Stai rispondendo al centralino aziendale. I chiamanti possono essere clienti, fornitori o nuovi contatti. Hai accesso alla rubrica aziendale e al calendario degli uffici.',
      tone: 'Rispondi in modo cordiale e professionale (1-2 frasi). Usa formule di cortesia appropriate. Chiedi sempre il nome dell\'interlocutore prima di trasferire.',
      goal: '1. Accogli il chiamante con il saluto aziendale\n2. Chiedi il nome e il motivo della chiamata\n3. Identifica il reparto o la persona richiesta\n4. Trasferisci la chiamata o prendi un messaggio se l\'interno è occupato\nQuesto passaggio è importante: verifica sempre la disponibilità prima di trasferire.',
      guardrails: 'Non fornire mai numeri di cellulare personali dei dipendenti.\nNon trasferire chiamate senza chiedere il motivo.\nSe il chiamante è aggressivo, mantieni la calma e offri di lasciare un messaggio.\nNon condividere informazioni riservate sull\'azienda.',
      error_handling: 'Se non riesci a identificare il reparto giusto:\n1. Chiedi più dettagli sul motivo della chiamata\n2. Offri i reparti disponibili tra cui scegliere\n3. In caso di dubbio, trasferisci alla segreteria generale',
      normalization: 'Numeri di telefono:\n- Parlato: "tre tre nove, uno due tre, quattro cinque sei sette"\n- Scritto: "+39 123 4567"\nEmail:\n- Parlato: "info chiocciola azienda punto it"\n- Scritto: "info@azienda.it"',
    }
  },
  {
    id: 'customer_service',
    label: 'Assistenza Clienti',
    description: 'Supporto e risoluzione problemi',
    sections: {
      personality: 'Sei uno specialista dell\'assistenza clienti per [Nome Azienda]. Sei empatico, paziente e orientato alla soluzione dei problemi.',
      environment: 'Stai assistendo clienti via telefono che hanno problemi con prodotti o servizi. Potresti incontrare clienti frustrati. Hai accesso al database ordini e al sistema di ticketing.',
      tone: 'Usa un tono calmo e rassicurante. Risposte chiare e concise (2-3 frasi). Conferma sempre di aver compreso il problema prima di proporre soluzioni. Usa brevi conferme: "Capisco", "Verifico subito".',
      goal: '1. Verifica l\'identità del cliente con numero ordine ed email\n2. Ascolta e comprendi il problema\n3. Cerca l\'ordine o il ticket nel sistema\n4. Proponi una soluzione o escalation al supervisore\nQuesto passaggio è importante: non fornire mai soluzioni senza aver prima verificato i dati.',
      guardrails: 'Non accedere ai dati del cliente senza verifica identità. Questo passaggio è importante.\nNon promettere rimborsi superiori a €100 senza approvazione del supervisore.\nNon fare promesse su date di consegna non confermate dal sistema.\nAmmetti quando non conosci la risposta invece di indovinare.',
      error_handling: 'Se un tool fallisce:\n1. Comunica: "Sto avendo difficoltà ad accedere alle informazioni in questo momento."\n2. Non inventare dati\n3. Riprova una volta\n4. Se il problema persiste, offri escalation: "Posso trasferirla a un collega specializzato."',
      normalization: 'Codici ordine:\n- Parlato: "O R D uno due tre quattro cinque sei"\n- Scritto: "ORD123456"\nEmail:\n- Parlato: "mario punto rossi chiocciola email punto it"\n- Scritto: "mario.rossi@email.it"',
    }
  },
  {
    id: 'tech_support',
    label: 'Supporto Tecnico',
    description: 'Troubleshooting tecnico guidato',
    sections: {
      personality: 'Sei uno specialista del supporto tecnico per [Nome Azienda]. Sei paziente, metodico e concentrato sulla risoluzione efficiente dei problemi tecnici. Adatti il linguaggio tecnico in base alla familiarità dell\'utente.',
      environment: 'Stai assistendo clienti con problemi tecnici su software/servizi. I clienti possono essere frustrati da disservizi. Hai accesso a strumenti diagnostici e al database degli account.',
      tone: 'Risposte chiare e concise (2-3 frasi, salvo troubleshooting che richiede più dettaglio). Tono calmo e professionale con brevi conferme. Adatta la profondità tecnica in base alle risposte del cliente. Verifica la comprensione dopo passaggi complessi: "È tutto chiaro?"',
      goal: '1. Verifica l\'identità del cliente con email e ID account\n2. Identifica il servizio interessato e il livello di gravità\n3. Esegui la diagnostica con il tool appropriato\n4. Fornisci risoluzione step-by-step o escalation dopo 2 tentativi\nQuesto passaggio è importante: esegui sempre la diagnostica prima di suggerire soluzioni.',
      guardrails: 'Non accedere mai agli account senza verifica identità. Questo passaggio è importante.\nNon indovinare soluzioni — basa sempre le raccomandazioni sui risultati diagnostici.\nSe il problema persiste dopo 2 tentativi di troubleshooting, escalation al team engineering.\nAmmetti quando non conosci la risposta invece di speculare.',
      error_handling: 'Se la diagnostica fallisce:\n1. Comunica il problema: "Sto avendo difficoltà ad eseguire il test diagnostico."\n2. Non inventare risultati\n3. Offri di riprovare o escalation: "Posso trasferirla al team tecnico di secondo livello."',
      normalization: 'Email:\n- Parlato: "utente punto test chiocciola azienda punto com"\n- Scritto: "utente.test@azienda.com"\n- Converti "chiocciola" → "@", "punto" → "."\nCodici errore:\n- Parlato: "E R R quattro zero tre"\n- Scritto: "ERR403"',
    }
  },
  {
    id: 'booking',
    label: 'Prenotazioni',
    description: 'Gestione appuntamenti e prenotazioni',
    sections: {
      personality: 'Sei un assistente per le prenotazioni di [Nome Azienda]. Sei efficiente, organizzato e premuroso nel trovare la soluzione migliore per le esigenze del cliente.',
      environment: 'Stai gestendo prenotazioni telefoniche. I clienti chiamano per prenotare, modificare o cancellare appuntamenti. Hai accesso al calendario disponibilità e al database clienti.',
      tone: 'Tono cordiale e propositivo. Risposte brevi e dirette (1-2 frasi). Offri sempre alternative quando la prima scelta non è disponibile. Conferma sempre i dettagli prima di finalizzare.',
      goal: '1. Identifica se il cliente vuole prenotare, modificare o cancellare\n2. Raccogli i dati necessari (nome, data, ora, servizio)\n3. Verifica la disponibilità nel calendario\n4. Conferma tutti i dettagli con il cliente prima di salvare\nQuesto passaggio è importante: ripeti sempre data, ora e servizio per conferma.',
      guardrails: 'Non confermare mai una prenotazione senza verificare la disponibilità reale.\nNon cancellare prenotazioni senza conferma esplicita del cliente.\nNon accedere ai dati del cliente senza verifica identità.\nRispetta le policy di cancellazione (minimo 24h di anticipo).',
      error_handling: 'Se il sistema di prenotazione non risponde:\n1. Comunica: "Sto avendo difficoltà ad accedere al calendario in questo momento."\n2. Offri di richiamare o lasciare i dati per una conferma successiva\n3. Non confermare disponibilità senza aver verificato nel sistema',
      normalization: 'Date:\n- Parlato: "venticinque marzo duemilaventisei"\n- Scritto: "2026-03-25"\nOrari:\n- Parlato: "alle quattordici e trenta"\n- Scritto: "14:30"\nNumeri telefono:\n- Parlato: "tre tre nove, cinque cinque cinque, uno due tre quattro"\n- Scritto: "+39 555 1234"',
    }
  },
];

const VOICE_PARAM_TOOLTIPS = {
  stability: 'Controlla la coerenza e prevedibilità della voce. Valori ALTI (>0.7): voce più stabile e uniforme, ideale per contesti professionali e lettura di testi. Valori BASSI (<0.3): maggiore variabilità ed espressività emotiva, più naturale ma meno prevedibile. Consigliato: 0.5 per un buon equilibrio.',
  similarity: 'Quanto la voce generata deve essere fedele alla voce originale. Valori ALTI (>0.8): massima fedeltà al campione vocale originale, ma può amplificare artefatti audio. Valori BASSI (<0.5): voce più "libera" e fluida, ma meno riconoscibile. Consigliato: 0.75 per alta fedeltà senza artefatti.',
};

interface FormData {
  name: string;
  description: string;
  language: string;
  first_message: string;
  is_active: boolean;
  llm_provider: string;
  llm_model: string;
  custom_llm_url: string;
  custom_endpoint_id: string | null;
  custom_model_id: string | null;
  prompt: string;
  temperature: number;
  max_tokens: number;
  voice_id: string;
  voice_name: string;
  voice_public_owner_id: string;
  voice_stability: number;
  voice_similarity: number;
  tts_model: string;
  tags: string[];
  max_duration_seconds: number;
  metadata: ElevenLabsAgentMetadata;
  approval_mode: string;
  rag_enabled: boolean;
  rag_inferential_enabled: boolean;
  rag_synthesis_mode: string;
  transcription_enabled: boolean;
  sentiment_enabled: boolean;
  context_memory_enabled: boolean;
  semantic_memory_enabled: boolean;
  channel_sip: boolean;
  channel_webrtc: boolean;
  dynamic_context_enabled: boolean;
  flow_definition?: FlowDefinition | null;
  pre_call_hooks: PreCallHooksConfig;
}

const defaultFormData: FormData = {
  name: '',
  description: '',
  language: 'it',
  first_message: 'Buongiorno! Come posso aiutarla?',
  is_active: true,
  llm_provider: 'openai',
  llm_model: 'gpt-5.4',
  custom_llm_url: '',
  custom_endpoint_id: null,
  custom_model_id: null,
  prompt: '',
  temperature: 0.7,
  max_tokens: 4096,
  voice_id: '',
  voice_name: '',
  voice_public_owner_id: '',
  voice_stability: 0.5,
  voice_similarity: 0.75,
  tts_model: 'eleven_flash_v2_5',
  tags: [],
  max_duration_seconds: 600,
  metadata: {},
  approval_mode: 'always',
  rag_enabled: false,
  rag_inferential_enabled: false,
  rag_synthesis_mode: 'native',
  transcription_enabled: false,
  sentiment_enabled: false,
  context_memory_enabled: false,
  semantic_memory_enabled: false,
  channel_sip: false,
  channel_webrtc: true,
  dynamic_context_enabled: true,
  pre_call_hooks: {
    inject_datetime: true,
    auto_crm_lookup: true,
    inject_caller_number: true,
  },
};

export default function ElevenLabsAgentConfigModal({
  open,
  onOpenChange,
  agent,
  onSaved
}: ElevenLabsAgentConfigModalProps) {
  const { toast } = useToast();
  const [currentStep, setCurrentStep] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState<FormData>(defaultFormData);
  const [claudeConfigStatus, setClaudeConfigStatus] = useState<{ configured: boolean; model?: string; hint?: string } | null>(null);
  const [geminiConfigStatus, setGeminiConfigStatus] = useState<{ configured: boolean; hint?: string } | null>(null);

  const [voices, setVoices] = useState<VoiceItem[]>([]);
  const [voiceSearch, setVoiceSearch] = useState('');
  const [voiceCategory, setVoiceCategory] = useState('all');
  const [voiceSource, setVoiceSource] = useState<'all' | 'account' | 'library'>('all');
  const [voiceLanguage, setVoiceLanguage] = useState('it');
  const [isLoadingVoices, setIsLoadingVoices] = useState(false);
  const [voicesError, setVoicesError] = useState<string | null>(null);
  const [playingVoiceId, setPlayingVoiceId] = useState<string | null>(null);
  const [audioElement, setAudioElement] = useState<HTMLAudioElement | null>(null);

  const [showCloneSection, setShowCloneSection] = useState(false);
  const [cloneName, setCloneName] = useState('');
  const [cloneFile, setCloneFile] = useState<File | null>(null);



  const [copiedEmbed, setCopiedEmbed] = useState(false);
  const [apiProviderModels, setApiProviderModels] = useState<Record<string, { id: string; name: string; desc?: string; badge?: string }[]>>({});
  const [apiProviderModelsOffline, setApiProviderModelsOffline] = useState<Record<string, boolean>>({});
  const [modelsLoading, setModelsLoading] = useState(false);
  const fetchRequestRef = useRef(0);
  const queryClient = useQueryClient();

  // Routes through the shared catalog fetcher + cache key (Task #1355/1066).
  // Imperative shape is preserved here only because we need autoSelectFirst
  // on tab change.
  const fetchProviderModels = useCallback(async (provider: string, autoSelectFirst = true) => {
    if (provider === 'elevenlabs' || provider === 'custom') return;
    const requestId = ++fetchRequestRef.current;
    setModelsLoading(true);
    try {
      const data = await queryClient.fetchQuery({
        queryKey: chatModelOptionsQueryKey(provider as LLMProvider),
        queryFn: () => fetchChatModelOptions(provider as LLMProvider),
        staleTime: 5 * 60 * 1000,
      });
      if (fetchRequestRef.current !== requestId) return;
      const list = data?.models ?? [];
      const mapped = list
        .filter((m) => m.status !== 'deprecated')
        .map((m) => ({
          id: m.id,
          name: m.displayName || m.id,
          desc: m.description,
          badge: m.isRecommended ? 'Consigliato' : m.status === 'preview' ? 'Preview' : undefined,
        }));
      if (mapped.length > 0) {
        setApiProviderModels(prev => ({ ...prev, [provider]: mapped }));
        if (autoSelectFirst && mapped[0]) {
          const recommended = mapped.find((m) => m.badge === 'Consigliato');
          const fallbackId = mapped[0].id;
          setFormData(prev => prev.llm_provider === provider ? { ...prev, llm_model: recommended?.id || fallbackId } : prev);
        }
      }
      setApiProviderModelsOffline(prev => ({ ...prev, [provider]: data?.source === 'fallback' }));
    } catch {
      setApiProviderModelsOffline(prev => ({ ...prev, [provider]: true }));
    } finally {
      if (fetchRequestRef.current === requestId) setModelsLoading(false);
    }
  }, [queryClient]);

  const [promptMode, setPromptMode] = useState<'builder' | 'editor' | 'flowBuilder'>('builder');
  const [promptSections, setPromptSections] = useState<PromptSections>({ ...emptyPromptSections });
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({ personality: true, goal: true, guardrails: true });
  const [showPromptPreview, setShowPromptPreview] = useState(false);

  const isEdit = !!agent?.id;

  const HEADING_MAP: Record<string, keyof PromptSections> = useMemo(() => ({
    'personality': 'personality', 'personalità': 'personality', 'personalita': 'personality',
    'environment': 'environment', 'ambiente': 'environment',
    'tone': 'tone', 'tono': 'tone',
    'goal': 'goal', 'obiettivo': 'goal',
    'guardrails': 'guardrails',
    'error handling': 'error_handling', 'gestione errori': 'error_handling', 'error_handling': 'error_handling',
    'character normalization': 'normalization', 'normalizzazione': 'normalization', 'normalization': 'normalization', 'normalizzazione caratteri': 'normalization',
  }), []);

  const matchHeading = useCallback((heading: string): keyof PromptSections | null => {
    const normalized = heading.trim().toLowerCase().replace(/[^a-zà-ú\s]/g, '').trim();
    if (HEADING_MAP[normalized]) return HEADING_MAP[normalized];
    for (const [key, val] of Object.entries(HEADING_MAP)) {
      if (normalized.startsWith(key) || normalized.includes(key)) return val;
    }
    return null;
  }, [HEADING_MAP]);

  const parsePromptToSections = useCallback((prompt: string): PromptSections => {
    const sections: PromptSections = { ...emptyPromptSections };
    if (!prompt) return sections;
    const lines = prompt.split('\n');
    let currentSection: keyof PromptSections | null = null;
    let currentContent: string[] = [];
    let hasAnyHeading = false;

    for (const line of lines) {
      const headingMatch = line.match(/^#{1,2}\s+(.+)$/i);
      if (headingMatch && headingMatch[1]) {
        if (currentSection && currentContent.length > 0) {
          sections[currentSection] = currentContent.join('\n').trim();
        }
        const matched = matchHeading(headingMatch[1]);
        if (matched) {
          hasAnyHeading = true;
          currentSection = matched;
          currentContent = [];
        } else {
          if (currentSection) {
            currentContent.push(line);
          }
        }
      } else if (currentSection) {
        currentContent.push(line);
      }
    }
    if (currentSection && currentContent.length > 0) {
      sections[currentSection] = currentContent.join('\n').trim();
    }
    if (!hasAnyHeading && prompt.trim()) {
      sections.personality = prompt.trim();
    }
    return sections;
  }, [matchHeading]);

  const assembleSectionsToPrompt = (sections: PromptSections): string => {
    const sectionLabels: Record<keyof PromptSections, string> = {
      personality: 'Personality',
      environment: 'Environment',
      tone: 'Tone',
      goal: 'Goal',
      guardrails: 'Guardrails',
      error_handling: 'Error handling',
      normalization: 'Character normalization',
    };
    return Object.entries(sections)
      .filter(([, val]) => val.trim().length > 0)
      .map(([key, val]) => `# ${sectionLabels[key as keyof PromptSections]}\n${val}`)
      .join('\n\n');
  };


  useEffect(() => {
    if (open) {
      if (agent) {
        const agentPrompt = agent.prompt || '';
        setFormData({
          name: agent.name || '',
          description: agent.description || '',
          language: agent.language || 'it',
          first_message: agent.first_message || 'Buongiorno! Come posso aiutarla?',
          is_active: agent.is_active,
          llm_provider: agent.llm_provider || 'openai',
          llm_model: agent.llm_model || 'gpt-5.4',
          custom_llm_url: agent.custom_llm_url || '',
          custom_endpoint_id: agent.custom_endpoint_id ?? null,
          custom_model_id: agent.custom_model_id ?? null,
          prompt: agentPrompt,
          temperature: parseFloat(String(agent.temperature ?? 0.7)),
          max_tokens: Math.min(parseInt(String(agent.max_tokens ?? 4096)), 4096),
          voice_id: agent.voice_id || '',
          voice_name: agent.voice_name || '',
          voice_public_owner_id: '',
          voice_stability: parseFloat(String(agent.voice_stability ?? 0.5)),
          voice_similarity: parseFloat(String(agent.voice_similarity ?? 0.75)),
          tts_model: agent.tts_model || 'eleven_flash_v2_5',
          tags: agent.tags || [],
          max_duration_seconds: agent.max_duration_seconds ?? 600,
          metadata: agent.metadata || {},
          approval_mode: agent.metadata?.approval_mode || 'always',
          rag_enabled: agent.rag_enabled ?? false,
          rag_inferential_enabled: agent.rag_inferential_enabled ?? false,
          rag_synthesis_mode: agent.rag_synthesis_mode || 'native',
          transcription_enabled: agent.transcription_enabled ?? false,
          sentiment_enabled: agent.sentiment_enabled ?? false,
          context_memory_enabled: agent.context_memory_enabled ?? false,
          semantic_memory_enabled: agent.semantic_memory_enabled ?? false,
          channel_sip: agent.channel_sip ?? false,
          channel_webrtc: agent.channel_webrtc ?? true,
          dynamic_context_enabled: agent.metadata?.dynamic_context_enabled ?? true,
          flow_definition: agent.flow_definition || null,
          pre_call_hooks: normalizePreCallHooks(agent.pre_call_hooks),
        });
        const parsed = parsePromptToSections(agentPrompt);
        setPromptSections(parsed);
        const parsedHasSections = Object.values(parsed).filter(v => v.trim().length > 0).length > 1 || (Object.values(parsed).some(v => v.trim().length > 0) && agentPrompt.match(/^#{1,2}\s+/m));
        setPromptMode(parsedHasSections ? 'builder' : 'editor');
      } else {
        setFormData(defaultFormData);
        setPromptSections({ ...emptyPromptSections });
        setPromptMode('builder');
      }
      setCurrentStep(0);
      setCopiedEmbed(false);
      setShowPromptPreview(false);
    }
  }, [open, agent, parsePromptToSections]);

  const loadVoices = useCallback(async () => {
    setIsLoadingVoices(true);
    setVoicesError(null);
    try {
      const params = new URLSearchParams();
      if (voiceSearch) params.set('search', voiceSearch);

      if (voiceSource === 'all') {
        const libraryParams = new URLSearchParams(params);
        if (voiceLanguage) libraryParams.set('language', voiceLanguage);
        libraryParams.set('page_size', '100');

        const [accountRes, libraryRes] = await Promise.allSettled([
          apiClient.get<VoiceItem[] | { voices?: VoiceItem[] }>(`/elevenlabs/voices?${params.toString()}`),
          apiClient.get<VoiceItem[] | { voices?: VoiceItem[] }>(`/elevenlabs/voices/library?${libraryParams.toString()}`),
        ]);

        const accountVoices: VoiceItem[] = [];
        if (accountRes.status === 'fulfilled') {
          const d = accountRes.value?.data ?? [];
          const arr = Array.isArray(d) ? d : d.voices || [];
          arr.forEach((v: VoiceItem) => { v._source = 'account'; });
          accountVoices.push(...arr);
        }

        const libraryVoices: VoiceItem[] = [];
        if (libraryRes.status === 'fulfilled') {
          const d = libraryRes.value?.data ?? {};
          const arr = Array.isArray(d) ? d : d.voices || [];
          arr.forEach((v: VoiceItem) => { v._source = 'library'; });
          libraryVoices.push(...arr);
        }

        const accountIds = new Set(accountVoices.map(v => v.voice_id));
        const uniqueLibrary = libraryVoices.filter(v => !accountIds.has(v.voice_id));
        setVoices([...accountVoices, ...uniqueLibrary]);

        if (accountVoices.length === 0 && libraryVoices.length === 0) {
          if (accountRes.status === 'rejected') {
            const errorMsg = accountRes.reason?.message || '';
            if (errorMsg.includes('401') || errorMsg.toLowerCase().includes('invalid_api_key')) {
              setVoicesError('API Key ElevenLabs non valida o non configurata. Vai nella tab "ElevenLabs" per inserire e salvare la chiave API.');
              return;
            }
          }
        }
      } else if (voiceSource === 'library') {
        if (voiceLanguage) params.set('language', voiceLanguage);
        params.set('page_size', '100');
        const res = await apiClient.get<VoiceItem[] | { voices?: VoiceItem[] }>(`/elevenlabs/voices/library?${params.toString()}`);
        const data = res?.data ?? {};
        const arr = Array.isArray(data) ? data : data.voices || [];
        arr.forEach((v: VoiceItem) => { v._source = 'library'; });
        setVoices(arr);
      } else {
        if (voiceCategory !== 'all') params.set('category', voiceCategory);
        const res = await apiClient.get<VoiceItem[] | { voices?: VoiceItem[] }>(`/elevenlabs/voices?${params.toString()}`);
        const data = res?.data ?? [];
        const arr = Array.isArray(data) ? data : data.voices || [];
        arr.forEach((v: VoiceItem) => { v._source = 'account'; });
        setVoices(arr);
      }
    } catch (error) {
      setVoices([]);
      const errorMsg = error instanceof Error ? error.message : '';
      if (errorMsg.includes('401') || errorMsg.toLowerCase().includes('invalid_api_key') || errorMsg.toLowerCase().includes('api key')) {
        setVoicesError('API Key ElevenLabs non valida o non configurata. Vai nella tab "ElevenLabs" per inserire e salvare la chiave API.');
        toast({ title: 'API Key non valida', description: 'Configura la chiave API ElevenLabs nella sezione dedicata prima di gestire gli agenti.', variant: 'destructive' });
      } else if (errorMsg.includes('No active ElevenLabs')) {
        setVoicesError('Nessuna configurazione ElevenLabs attiva. Vai nella tab "ElevenLabs" per configurare la connessione.');
        toast({ title: 'ElevenLabs non configurato', description: 'Configura la connessione ElevenLabs prima di gestire gli agenti.', variant: 'destructive' });
      } else {
        setVoicesError('Errore nel caricamento delle voci. Riprova.');
      }
    } finally {
      setIsLoadingVoices(false);
    }
  }, [toast, voiceCategory, voiceLanguage, voiceSearch, voiceSource]);

  useEffect(() => {
    if (open && currentStep === 2) {
      loadVoices();
    }
  }, [open, currentStep, voiceSearch, voiceCategory, voiceSource, voiceLanguage, loadVoices]);

  useEffect(() => {
    if (open && formData.llm_provider === 'claude') {
      apiClient.get<{ api_key_hint?: string; default_model?: string; [key: string]: unknown }>('/ai-tools/claude-config')
        .then(res => {
          const configData = extractProviderConfig(res);
          if (configData?.api_key_hint) {
            setClaudeConfigStatus({
              configured: true,
              model: configData.default_model,
              hint: configData.api_key_hint,
            });
          } else {
            setClaudeConfigStatus({ configured: false });
          }
        })
        .catch(() => setClaudeConfigStatus({ configured: false }));
    }

    if (open && formData.llm_provider === 'gemini') {
      apiClient.get<{ api_key_hint?: string; default_model?: string; [key: string]: unknown }>('/ai-tools/gemini-config')
        .then(res => {
          const configData = extractProviderConfig(res);
          if (configData?.api_key_hint) {
            setGeminiConfigStatus({
              configured: true,
              hint: configData.api_key_hint,
            });
          } else {
            setGeminiConfigStatus({ configured: false });
          }
        })
        .catch(() => setGeminiConfigStatus({ configured: false }));
    }

    if (open) {
      const p = formData.llm_provider;
      if (!apiProviderModels[p]) {
        void fetchProviderModels(p, false);
      }
    }
  }, [open, formData.llm_provider, apiProviderModels, fetchProviderModels]);

  const handlePlayVoice = (voice: VoiceItem) => {
    if (playingVoiceId === voice.voice_id) {
      audioElement?.pause();
      setPlayingVoiceId(null);
      return;
    }
    if (audioElement) {
      audioElement.pause();
    }
    if (voice.preview_url) {
      const audio = new Audio(voice.preview_url);
      audio.onended = () => setPlayingVoiceId(null);
      audio.play();
      setAudioElement(audio);
      setPlayingVoiceId(voice.voice_id);
    }
  };

  const validateStep = (_step: number): boolean => {
    return true;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, STEPS.length - 1));
    }
  };

  const handlePrev = () => {
    setCurrentStep(prev => Math.max(prev - 1, 0));
  };

  // Task #1316 — guard cambio chat model con doc multimodali Gemini.
  const kbGuard = useKbModelChangeGuard();

  const doHandleSave = async (opts: KbGuardedPerformSaveArgs): Promise<void> => {
    void opts;
    setIsSaving(true);
    try {
      const payload = {
        name: formData.name,
        description: formData.description,
        language: formData.language,
        first_message: formData.first_message,
        is_active: formData.is_active,
        llm_provider: formData.llm_provider,
        llm_model: formData.llm_model,
        custom_llm_url: formData.custom_llm_url || null,
        custom_endpoint_id: formData.llm_provider === 'custom' ? formData.custom_endpoint_id : null,
        custom_model_id: formData.llm_provider === 'custom' ? formData.custom_model_id : null,
        prompt: formData.prompt,
        temperature: formData.temperature,
        max_tokens: Math.min(formData.max_tokens, 4096),
        voice_id: formData.voice_id || null,
        voice_name: formData.voice_name || null,
        voice_public_owner_id: formData.voice_public_owner_id || null,
        voice_stability: formData.voice_stability,
        voice_similarity: formData.voice_similarity,
        tts_model: formData.tts_model,
        tags: formData.tags,
        max_duration_seconds: formData.max_duration_seconds,
        metadata: { ...formData.metadata, approval_mode: formData.approval_mode, dynamic_context_enabled: formData.dynamic_context_enabled },
        rag_enabled: formData.rag_enabled,
        rag_inferential_enabled: formData.rag_inferential_enabled,
        rag_synthesis_mode: formData.rag_synthesis_mode,
        transcription_enabled: formData.transcription_enabled,
        sentiment_enabled: formData.sentiment_enabled,
        context_memory_enabled: formData.context_memory_enabled,
        semantic_memory_enabled: formData.semantic_memory_enabled,
        channel_sip: formData.channel_sip,
        channel_webrtc: formData.channel_webrtc,
        flow_definition: formData.flow_definition || null,
        pre_call_hooks: formData.pre_call_hooks || {},
      };

      if (isEdit) {
        await apiClient.patch(`/elevenlabs/agents/${agent.id}`, payload);
        toast({ title: 'Successo', description: 'Agente aggiornato con successo' });
      } else {
        await apiClient.post('/elevenlabs/agents', payload);
        toast({ title: 'Successo', description: 'Agente creato con successo' });
      }
      onSaved();
      onOpenChange(false);
    } catch (error) {
      // Task #1435 — limite tenant agenti AI: modal globale già aperto.
      if (isTenantLimitHandled(error)) {
        onOpenChange(false);
        return;
      }
      const errObj = error as { response?: { data?: { error?: string } }; message?: string } | null;
      const description = errObj?.response?.data?.error
        || errObj?.message
        || (error instanceof Error ? error.message : 'Errore durante il salvataggio');
      toast({
        title: 'Errore',
        description,
        variant: 'destructive'
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast({ title: 'Errore', description: 'Il nome agente è obbligatorio', variant: 'destructive' });
      setCurrentStep(0);
      return;
    }

    await kbGuard.runGuardedSave({
      agentId: isEdit ? agent?.id ?? null : null,
      newChatModel: formData.llm_model,
      performSave: (opts) => doHandleSave(opts),
    });
  };

  const handleCopyEmbed = () => {
    const embedCode = `<script src="https://elevenlabs.io/convai-widget/index.js" async type="text/javascript"></script>\n<elevenlabs-convai agent-id="${agent?.elevenlabs_agent_id}"></elevenlabs-convai>`;
    navigator.clipboard.writeText(embedCode);
    setCopiedEmbed(true);
    toast({ title: 'Codice copiato!' });
    setTimeout(() => setCopiedEmbed(false), 2000);
  };

  const baseModels = apiProviderModels[formData.llm_provider] || MODELS_BY_PROVIDER[formData.llm_provider] || [];
  const models = formData.llm_model && !baseModels.some(m => m.id === formData.llm_model) && formData.llm_provider !== 'custom'
    ? [...baseModels, { id: formData.llm_model, name: formData.llm_model, desc: 'Modello configurato su ElevenLabs' }]
    : baseModels;

  const renderStepIndicator = () => (
    <div className="flex items-center justify-between px-2 mb-4">
      {STEPS.map((step, index) => {
        const Icon = step.icon;
        const isActive = index === currentStep;
        const isCompleted = index < currentStep;
        return (
          <div key={index} className="flex flex-col items-center gap-1 flex-1">
            <div className="flex items-center w-full">
              {index > 0 && (
                <div className={`flex-1 h-0.5 ${isCompleted ? 'bg-emerald-500' : 'bg-muted'}`} />
              )}
              <button
                type="button"
                onClick={() => {
                  if (index < currentStep || validateStep(currentStep)) {
                    setCurrentStep(index);
                  }
                }}
                className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium transition-colors ${
                  isActive
                    ? 'bg-emerald-500 text-white'
                    : isCompleted
                    ? 'bg-emerald-100 text-emerald-700'
                    : 'bg-muted text-muted-foreground'
                }`}
              >
                {isCompleted ? <Check className="h-4 w-4" /> : index + 1}
              </button>
              {index < STEPS.length - 1 && (
                <div className={`flex-1 h-0.5 ${isCompleted ? 'bg-emerald-500' : 'bg-muted'}`} />
              )}
            </div>
            <span className={`text-[10px] ${isActive ? 'text-emerald-600 font-medium' : 'text-muted-foreground'}`}>
              {step.label}
            </span>
          </div>
        );
      })}
    </div>
  );

  const renderStep0 = () => (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Nome Agente *</Label>
        <Input
          value={formData.name}
          onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
          placeholder="Receptionist Virtuale"
        />
      </div>

      <div className="space-y-2">
        <Label>Descrizione</Label>
        <Textarea
          value={formData.description}
          onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
          rows={3}
          placeholder="Agente vocale per accoglienza e smistamento chiamate"
        />
      </div>

      <div className="space-y-2">
        <Label>Lingua</Label>
        <Select
          value={formData.language}
          onValueChange={(value) => setFormData(prev => ({ ...prev, language: value }))}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {LANGUAGES.map(l => (
              <SelectItem key={l.id} value={l.id}>
                {l.flag} {l.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label>Messaggio Iniziale</Label>
        <Input
          value={formData.first_message}
          onChange={(e) => setFormData(prev => ({ ...prev, first_message: e.target.value }))}
          placeholder="Buongiorno! Come posso aiutarla?"
        />
      </div>

      <div className="flex items-center justify-between p-3 rounded-lg border border-border">
        <div>
          <Label>Knowledge Base RAG</Label>
          <p className="text-xs text-muted-foreground">Risposte basate sui tuoi documenti</p>
        </div>
        <Switch
          checked={formData.rag_enabled}
          onCheckedChange={(checked) => setFormData(prev => ({ ...prev, rag_enabled: checked }))}
        />
      </div>

      {formData.rag_enabled && (
        <div className="flex items-center justify-between p-3 rounded-lg border border-border ml-4 border-l-2 border-blue-300">
          <div className="flex items-center gap-1.5">
            <Label>Sintesi Nativa</Label>
            <div className="relative group">
              <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 w-64 p-2 bg-gray-900 text-white text-[10px] rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 pointer-events-none">
                <p className="font-medium mb-1">ON: L'agente ragiona direttamente sui dati recuperati dalla Knowledge Base. Più veloce, più naturale, costo inferiore.</p>
                <p>OFF: I dati vengono prima sintetizzati da un modello ausiliario (GPT-4o-mini) e poi passati all'agente. Più controllata, utile con modelli costosi.</p>
              </div>
            </div>
          </div>
          <Switch
            checked={formData.rag_synthesis_mode === 'native'}
            onCheckedChange={(checked) => setFormData(prev => ({ ...prev, rag_synthesis_mode: checked ? 'native' : 'pre_synthesized', rag_inferential_enabled: !checked }))}
          />
        </div>
      )}

      <div className="flex items-center justify-between p-3 bg-muted rounded-lg border">
        <div>
          <Label>Attivo</Label>
          <p className="text-xs text-muted-foreground">Abilita o disabilita l'agente</p>
        </div>
        <Switch
          checked={formData.is_active}
          onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
        />
      </div>

    </div>
  );

  const remoteConfig = agent?.elevenlabs_remote_config as { llm_provider: string; llm_model: string; tts_model: string; tools_count: number } | undefined;

  const renderStep1 = () => (
    <div className="space-y-4">
      {remoteConfig && (
        <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 text-sm space-y-1">
          <div className="flex items-center gap-2 font-medium text-blue-800">
            <Info className="h-4 w-4" />
            Config attiva su ElevenLabs
          </div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-xs text-blue-700 mt-1">
            <span>LLM: <strong>{remoteConfig.llm_model}</strong></span>
            <span>Provider: <strong>{remoteConfig.llm_provider}</strong></span>
            <span>TTS: <strong>{remoteConfig.tts_model}</strong></span>
            <span>Tools: <strong>{remoteConfig.tools_count}</strong></span>
          </div>
          {(remoteConfig.llm_model !== formData.llm_model || remoteConfig.llm_provider !== formData.llm_provider) && (
            <div className="flex items-center gap-1 text-xs text-amber-700 bg-amber-50 px-2 py-1 rounded mt-1">
              <AlertTriangle className="h-3 w-3" />
              Il modello su ElevenLabs ({remoteConfig.llm_model}) non corrisponde a quello selezionato ({formData.llm_model}). Salva per sincronizzare.
            </div>
          )}
        </div>
      )}
      <Label className="text-sm font-medium">Provider LLM</Label>
      <div className="grid grid-cols-2 gap-3">
        {PROVIDERS.map(p => {
          const Icon = p.icon;
          const isSelected = formData.llm_provider === p.id;
          return (
            <Card
              key={p.id}
              className={`cursor-pointer transition-all ${
                isSelected ? `border-2 border-emerald-500 ${p.bg}` : 'border hover:border-border'
              }`}
              onClick={() => {
                const cachedModels = apiProviderModels[p.id];
                const fallbackModels = MODELS_BY_PROVIDER[p.id];
                const firstModel = (cachedModels || fallbackModels)?.[0]?.id || '';
                setFormData(prev => ({
                  ...prev,
                  llm_provider: p.id,
                  llm_model: p.id === 'custom' ? '' : firstModel,
                  custom_endpoint_id: p.id === 'custom' ? prev.custom_endpoint_id : null,
                  custom_model_id: p.id === 'custom' ? prev.custom_model_id : null,
                }));
                if (!cachedModels || cachedModels.length === 0) {
                  fetchProviderModels(p.id, true);
                }
              }}
            >
              <CardContent className="p-3 flex items-start gap-3">
                <div className={`p-2 rounded-lg ${p.bg}`}>
                  <Icon className={`h-5 w-5 ${p.color}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm">{p.label}</span>
                    {isSelected && <Check className="h-4 w-4 text-emerald-500" />}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">{p.desc}</p>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {formData.llm_provider === 'claude' && (
        <div className="space-y-3">
          {claudeConfigStatus?.configured ? (
            <div className="flex items-center gap-2 p-3 bg-purple-50 rounded-lg border border-purple-200 text-sm text-purple-800">
              <CheckCircle className="h-4 w-4 flex-shrink-0 text-purple-600" />
              <div>
                <span className="font-medium">API Key Anthropic ereditata</span>
                <span className="text-purple-600 ml-1">({claudeConfigStatus.hint})</span>
                <p className="text-xs text-purple-500 mt-0.5">La chiave viene presa dalla configurazione Claude centralizzata in AI Tools.</p>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-lg border border-amber-200 text-sm text-amber-800">
              <AlertTriangle className="h-4 w-4 flex-shrink-0 text-amber-600" />
              <div>
                <span className="font-medium">Configurazione Claude non trovata</span>
                <p className="text-xs text-amber-600 mt-0.5">Configura prima la API key Anthropic nella sezione AI Tools &rarr; Claude AI per usare Claude come LLM.</p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 bg-muted rounded-lg border">
              <div className="flex items-center gap-1.5 mb-1">
                <Brain className="h-3.5 w-3.5 text-purple-500" />
                <span className="text-xs font-medium text-foreground">Context Window</span>
              </div>
              <p className="text-sm font-semibold">Fino a 1M tokens</p>
              <p className="text-[10px] text-muted-foreground">Modelli 4.x Sonnet/Opus</p>
            </div>
            <div className="p-3 bg-muted rounded-lg border">
              <div className="flex items-center gap-1.5 mb-1">
                <Wrench className="h-3.5 w-3.5 text-purple-500" />
                <span className="text-xs font-medium text-foreground">Tool Calling</span>
              </div>
              <p className="text-sm font-semibold">Nativo</p>
              <p className="text-[10px] text-muted-foreground">Gestito da ElevenLabs</p>
            </div>
          </div>

          <div className="flex items-start gap-1.5 p-2.5 bg-blue-50 rounded-lg border border-blue-100 text-xs text-blue-700">
            <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
            <span>Quando Claude opera dietro ElevenLabs, i tools e MCP sono gestiti da ElevenLabs. Le Skills native Anthropic non sono disponibili in questa modalita (usabili solo nella chat Claude diretta).</span>
          </div>
        </div>
      )}

      {formData.llm_provider === 'gemini' && (
        <div className="space-y-3">
          {geminiConfigStatus?.configured ? (
            <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg border border-green-200 text-sm text-green-800">
              <CheckCircle className="h-4 w-4 flex-shrink-0 text-green-600" />
              <div>
                <span className="font-medium">API Key Google ereditata</span>
                <span className="text-green-600 ml-1">({geminiConfigStatus.hint})</span>
                <p className="text-xs text-green-500 mt-0.5">La chiave viene presa dalla configurazione Gemini centralizzata in AI Tools.</p>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-lg border border-amber-200 text-sm text-amber-800">
              <AlertTriangle className="h-4 w-4 flex-shrink-0 text-amber-600" />
              <div>
                <span className="font-medium">Configurazione Gemini non trovata</span>
                <p className="text-xs text-amber-600 mt-0.5">Configura prima la API key Google nella sezione AI Tools &rarr; Gemini AI per usare Gemini come LLM.</p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 bg-muted rounded-lg border">
              <div className="flex items-center gap-1.5 mb-1">
                <Brain className="h-3.5 w-3.5 text-green-500" />
                <span className="text-xs font-medium text-foreground">Context Window</span>
              </div>
              <p className="text-sm font-semibold">Fino a 1M tokens</p>
              <p className="text-[10px] text-muted-foreground">Modelli 2.5 Flash/Pro</p>
            </div>
            <div className="p-3 bg-muted rounded-lg border">
              <div className="flex items-center gap-1.5 mb-1">
                <Wrench className="h-3.5 w-3.5 text-green-500" />
                <span className="text-xs font-medium text-foreground">Function Calling</span>
              </div>
              <p className="text-sm font-semibold">Nativo</p>
              <p className="text-[10px] text-muted-foreground">Gestito da ElevenLabs</p>
            </div>
          </div>

          <div className="flex items-start gap-1.5 p-2.5 bg-blue-50 rounded-lg border border-blue-100 text-xs text-blue-700">
            <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
            <span>Quando Gemini opera dietro ElevenLabs, il function calling è gestito da ElevenLabs. I tools nativi Google (Search, Code Execution) non sono disponibili — usa tools personalizzati ElevenLabs per funzionalità equivalenti.</span>
          </div>
        </div>
      )}

      {formData.llm_provider !== 'elevenlabs' && (
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <Label>Modello</Label>
            {apiProviderModelsOffline[formData.llm_provider] && (
              <span className="text-[10px] px-1.5 py-0 rounded border border-amber-300 bg-amber-50 text-amber-700" title="La lista modelli proviene dal fallback locale: la chiamata API al provider non è andata a buon fine.">⚠️ Lista offline — verifica chiave API</span>
            )}
          </div>
          {formData.llm_provider === 'custom' ? (
            <CustomLLMPicker
              endpointId={formData.custom_endpoint_id}
              modelId={formData.custom_model_id}
              modelName={formData.llm_model}
              onChange={(endpointId, modelId, modelName) => setFormData(prev => ({
                ...prev,
                custom_endpoint_id: endpointId,
                custom_model_id: modelId,
                llm_model: modelName,
              }))}
            />
          ) : (
            <div className="space-y-1.5">
              <Select
                value={formData.llm_model}
                onValueChange={(value) => setFormData(prev => ({ ...prev, llm_model: value }))}
              >
                <SelectTrigger>
                  {modelsLoading
                    ? <span className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-3.5 w-3.5 animate-spin" />Caricamento modelli dal provider...</span>
                    : <SelectValue placeholder="Seleziona un modello..." />
                  }
                </SelectTrigger>
                <SelectContent>
                  {models.map((m: ModelOption) => (
                    <SelectItem key={m.id} value={m.id}>
                      <div className="flex items-center gap-2">
                        <span>{m.name}</span>
                        {m.badge && (
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                            m.badge === 'Consigliato' ? 'bg-emerald-100 text-emerald-700' :
                            m.badge === 'Veloce' ? 'bg-blue-100 text-blue-700' :
                            m.badge === 'Nuovo' ? 'bg-purple-100 text-purple-700' :
                            m.badge === 'Reasoning' ? 'bg-amber-100 text-amber-700' :
                            m.badge === 'Premium' ? 'bg-purple-100 text-purple-700' :
                            'bg-muted text-muted-foreground'
                          }`}>{m.badge}</span>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {(() => {
                const selectedModel = (models as ModelOption[]).find((m: ModelOption) => m.id === formData.llm_model);
                return selectedModel?.desc ? (
                  <p className="text-xs text-muted-foreground px-1">{selectedModel.desc}</p>
                ) : null;
              })()}
            </div>
          )}
        </div>
      )}

      {formData.llm_provider === 'custom' && (
        <div className="rounded-md border border-blue-200 bg-blue-50 dark:bg-blue-950/30 dark:border-blue-800 p-3 text-xs text-blue-700 dark:text-blue-300 flex items-start gap-1.5">
          <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
          <span>URL e API key vengono ereditati dall&apos;endpoint Custom AI selezionato. Gestiscili in <strong>AI Tools → Custom AI</strong>.</span>
        </div>
      )}

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label>Temperatura: {Number(formData.temperature).toFixed(1)}</Label>
          <span className="text-xs text-muted-foreground">
            {Number(formData.temperature) < 0.3 ? 'Preciso' : Number(formData.temperature) > 0.7 ? 'Creativo' : 'Bilanciato'}
          </span>
        </div>
        <input
          type="range"
          value={formData.temperature}
          onChange={(e) => setFormData(prev => ({ ...prev, temperature: parseFloat(e.target.value) }))}
          min={0}
          max={1}
          step={0.1}
          className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-emerald-500"
        />
      </div>

      <div className="space-y-2">
        <Label>Max Tokens</Label>
        <Input
          type="number"
          value={formData.max_tokens}
          onChange={(e) => setFormData(prev => ({ ...prev, max_tokens: Math.min(parseInt(e.target.value) || 4096, 4096) }))}
          min={256}
          max={4096}
        />
        <p className="text-[10px] text-muted-foreground">Massimo 4096 (limite API ElevenLabs)</p>
      </div>


      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label>Durata Massima: {Math.floor(formData.max_duration_seconds / 60)} min</Label>
          <span className="text-xs text-muted-foreground">
            {formData.max_duration_seconds <= 300 ? 'Breve' : formData.max_duration_seconds <= 600 ? 'Standard' : 'Estesa'}
          </span>
        </div>
        <input
          type="range"
          value={formData.max_duration_seconds}
          onChange={(e) => setFormData(prev => ({ ...prev, max_duration_seconds: parseInt(e.target.value) }))}
          min={60}
          max={1800}
          step={60}
          className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-emerald-500"
        />
        <p className="text-[10px] text-muted-foreground">Durata massima della conversazione (1-30 minuti)</p>
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label className="font-medium">Prompt di Sistema</Label>
          <div className="flex items-center gap-2">
            <div className="flex bg-muted rounded-lg p-0.5">
              <button
                type="button"
                onClick={() => {
                  if (promptMode === 'editor') {
                    const parsed = parsePromptToSections(formData.prompt);
                    setPromptSections(parsed);
                  }
                  setPromptMode('builder');
                }}
                className={`text-xs px-3 py-1 rounded-md transition-colors flex items-center gap-1 ${promptMode === 'builder' ? 'bg-emerald-500 text-white' : 'text-muted-foreground hover:text-foreground'}`}
              >
                <Sparkles className="h-3 w-3" /> Builder
              </button>
              <button
                type="button"
                onClick={() => {
                  if (promptMode === 'builder') {
                    const assembled = assembleSectionsToPrompt(promptSections);
                    setFormData(prev => ({ ...prev, prompt: assembled }));
                  }
                  setPromptMode('editor');
                }}
                className={`text-xs px-3 py-1 rounded-md transition-colors flex items-center gap-1 ${promptMode === 'editor' ? 'bg-emerald-500 text-white' : 'text-muted-foreground hover:text-foreground'}`}
              >
                <Pencil className="h-3 w-3" /> Editor Libero
              </button>
              <button
                type="button"
                onClick={() => {
                  if (promptMode === 'builder') {
                    const assembled = assembleSectionsToPrompt(promptSections);
                    setFormData(prev => ({ ...prev, prompt: assembled }));
                  }
                  setPromptMode('flowBuilder');
                }}
                className={`text-xs px-3 py-1 rounded-md transition-colors flex items-center gap-1 ${promptMode === 'flowBuilder' ? 'bg-emerald-500 text-white' : 'text-muted-foreground hover:text-foreground'}`}
              >
                <GitBranch className="h-3 w-3" /> Flow Builder
              </button>
            </div>
          </div>
        </div>

        {promptMode === 'builder' && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Select
                value=""
                onValueChange={(templateId) => {
                  const tpl = PROMPT_TEMPLATES.find(t => t.id === templateId);
                  if (tpl) {
                    setPromptSections({ ...tpl.sections });
                    const assembled = assembleSectionsToPrompt(tpl.sections);
                    setFormData(prev => ({ ...prev, prompt: assembled }));
                    setExpandedSections({ personality: true, goal: true, guardrails: true });
                  }
                }}
              >
                <SelectTrigger className="h-8 text-xs flex-1">
                  <SelectValue placeholder="Carica un template preconfigurato..." />
                </SelectTrigger>
                <SelectContent>
                  {PROMPT_TEMPLATES.map(tpl => (
                    <SelectItem key={tpl.id} value={tpl.id}>
                      <div className="flex flex-col">
                        <span className="font-medium">{tpl.label}</span>
                        <span className="text-[10px] text-muted-foreground">{tpl.description}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={() => setShowPromptPreview(!showPromptPreview)}
                      className={`h-8 w-8 flex items-center justify-center rounded-md border transition-colors ${showPromptPreview ? 'bg-emerald-50 border-emerald-300 text-emerald-600' : 'bg-card border-border text-muted-foreground hover:text-foreground'}`}
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="left" className="max-w-xs">
                    <p>Mostra l'anteprima del prompt assemblato in formato markdown</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>

            {showPromptPreview && (
              <div className="p-3 bg-muted rounded-lg border text-xs font-mono whitespace-pre-wrap max-h-48 overflow-y-auto">
                {assembleSectionsToPrompt(promptSections) || <span className="text-muted-foreground italic">Nessun contenuto nel prompt. Compila le sezioni qui sotto.</span>}
              </div>
            )}

            <div className="space-y-1.5">
              {PROMPT_SECTIONS.map(section => {
                const SectionIcon = section.icon;
                const isExpanded = expandedSections[section.id] ?? false;
                const sectionValue = promptSections[section.id as keyof PromptSections] || '';
                const hasContent = sectionValue.trim().length > 0;
                return (
                  <div key={section.id} className={`rounded-lg border transition-colors ${hasContent ? 'border-emerald-200 bg-emerald-50/30' : 'border-border'}`}>
                    <button
                      type="button"
                      onClick={() => setExpandedSections(prev => ({ ...prev, [section.id]: !isExpanded }))}
                      className="w-full flex items-center justify-between p-2.5 hover:bg-muted/50 rounded-lg transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <SectionIcon className={`h-4 w-4 ${hasContent ? 'text-emerald-500' : 'text-muted-foreground'}`} />
                        <span className={`text-sm font-medium ${hasContent ? 'text-foreground' : 'text-muted-foreground'}`}>{section.label}</span>
                        {hasContent && <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />}
                      </div>
                      <div className="flex items-center gap-1">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span
                                onClick={(e) => e.stopPropagation()}
                                className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-muted-foreground cursor-help"
                              >
                                <Info className="h-3.5 w-3.5" />
                              </span>
                            </TooltipTrigger>
                            <TooltipContent side="left" className="max-w-sm text-xs leading-relaxed">
                              <p>{section.tooltip}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                        {isExpanded ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
                      </div>
                    </button>
                    {isExpanded && (
                      <div className="px-2.5 pb-2.5">
                        <Textarea
                          value={sectionValue}
                          onChange={(e) => {
                            const newSections = { ...promptSections, [section.id]: e.target.value };
                            setPromptSections(newSections);
                            const assembled = assembleSectionsToPrompt(newSections);
                            setFormData(prev => ({ ...prev, prompt: assembled }));
                          }}
                          placeholder={section.placeholder}
                          rows={3}
                          className="resize-y min-h-[72px] text-sm"
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {promptMode === 'editor' && (
          <div>
            <Textarea
              value={formData.prompt}
              onChange={(e) => setFormData(prev => ({ ...prev, prompt: e.target.value }))}
              placeholder="# Personality\nSei un assistente virtuale professionale...\n\n# Goal\n1. Verifica l'identità del cliente\n2. Risolvi il problema\n\n# Guardrails\nNon inventare mai informazioni."
              rows={12}
              className="resize-y min-h-[200px] font-mono text-sm"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Usa intestazioni markdown (# Personality, # Goal, # Guardrails, ecc.) per strutturare il prompt secondo le best practice ElevenLabs.
            </p>
          </div>
        )}

        {promptMode === 'flowBuilder' && (
          <div className="border rounded-lg p-4 bg-muted text-center">
            <GitBranch className="h-8 w-8 text-indigo-500 mx-auto mb-2" />
            <p className="text-sm text-muted-foreground mb-3">
              Usa il Flow Builder visuale per definire il flusso conversazionale dell'agente.
            </p>
            <p className="text-xs text-muted-foreground mb-3">
              Il prompt verrà generato automaticamente dal flusso.
            </p>
          </div>
        )}

      </div>

      <Card className="border-violet-200 bg-violet-50/30">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Zap className="w-4 h-4 text-violet-600" />
              <Label className="text-sm font-medium">Contesto Dinamico</Label>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="w-3.5 h-3.5 text-muted-foreground cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-sm">
                    <p className="text-xs">
                      Abilita il tool <strong>get_caller_context</strong> che fornisce all'agente ElevenLabs informazioni in tempo reale: data/ora, dati CRM del chiamante, storico interazioni e orari aziendali.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <Switch
              checked={formData.dynamic_context_enabled}
              onCheckedChange={(checked) => setFormData(prev => ({ ...prev, dynamic_context_enabled: checked }))}
            />
          </div>
          {formData.dynamic_context_enabled && (
            <div className="bg-card rounded-lg border p-3 space-y-2">
              <p className="text-xs text-muted-foreground">
                Aggiungi questa istruzione nel prompt dell'agente per attivare il contesto automatico:
              </p>
              <div className="bg-muted rounded p-2 border font-mono text-xs text-foreground">
                Prima di iniziare la conversazione, chiama il tool <strong>get_caller_context</strong> per ottenere informazioni sul chiamante e il contesto corrente.
              </div>
              <p className="text-xs text-muted-foreground">
                Il tool raccoglie automaticamente: data/ora, informazioni CRM, storico chiamate, orari aziendali e contesto geografico.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-4">
      <div className="flex gap-2 items-center">
        <div className="flex bg-muted rounded-lg p-0.5">
          <Button
            type="button"
            size="sm"
            variant={voiceSource === 'all' ? 'default' : 'ghost'}
            onClick={() => { setVoiceSource('all'); setVoiceCategory('all'); }}
            className={`text-xs px-3 ${voiceSource === 'all' ? 'bg-emerald-500 hover:bg-emerald-600 text-white' : 'text-muted-foreground'}`}
          >
            Tutte
          </Button>
          <Button
            type="button"
            size="sm"
            variant={voiceSource === 'account' ? 'default' : 'ghost'}
            onClick={() => { setVoiceSource('account'); setVoiceCategory('all'); }}
            className={`text-xs px-3 ${voiceSource === 'account' ? 'bg-emerald-500 hover:bg-emerald-600 text-white' : 'text-muted-foreground'}`}
          >
            <Mic className="h-3 w-3 mr-1" />
            Le mie voci
          </Button>
          <Button
            type="button"
            size="sm"
            variant={voiceSource === 'library' ? 'default' : 'ghost'}
            onClick={() => { setVoiceSource('library'); setVoiceCategory('all'); }}
            className={`text-xs px-3 ${voiceSource === 'library' ? 'bg-emerald-500 hover:bg-emerald-600 text-white' : 'text-muted-foreground'}`}
          >
            <Globe className="h-3 w-3 mr-1" />
            Libreria
          </Button>
        </div>
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={voiceSearch}
            onChange={(e) => setVoiceSearch(e.target.value)}
            placeholder="Cerca voce..."
            className="pl-9"
          />
        </div>
      </div>

      <div className="flex gap-1 flex-wrap items-center">
        {(voiceSource === 'library' || voiceSource === 'all') && (
          <Select value={voiceLanguage} onValueChange={setVoiceLanguage}>
            <SelectTrigger className="w-[140px] h-8 text-xs">
              <SelectValue placeholder="Lingua" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="it">Italiano</SelectItem>
              <SelectItem value="en">English</SelectItem>
              <SelectItem value="es">Espanol</SelectItem>
              <SelectItem value="fr">Francais</SelectItem>
              <SelectItem value="de">Deutsch</SelectItem>
              <SelectItem value="pt">Portugues</SelectItem>
              <SelectItem value="pl">Polski</SelectItem>
              <SelectItem value="hi">Hindi</SelectItem>
              <SelectItem value="ar">Arabic</SelectItem>
              <SelectItem value="ja">Japanese</SelectItem>
              <SelectItem value="ko">Korean</SelectItem>
              <SelectItem value="zh">Chinese</SelectItem>
              <SelectItem value="ru">Russian</SelectItem>
              <SelectItem value="nl">Dutch</SelectItem>
              <SelectItem value="sv">Swedish</SelectItem>
              <SelectItem value="tr">Turkish</SelectItem>
            </SelectContent>
          </Select>
        )}
        {voiceSource === 'account' && VOICE_CATEGORIES.map(cat => (
          <Button
            key={cat.id}
            type="button"
            variant={voiceCategory === cat.id ? 'default' : 'outline'}
            size="sm"
            onClick={() => setVoiceCategory(cat.id)}
            className={`text-xs ${voiceCategory === cat.id ? 'bg-emerald-500 hover:bg-emerald-600' : ''}`}
          >
            {cat.label}
          </Button>
        ))}
        <span className="text-xs text-muted-foreground ml-auto">{voices.length} voci</span>
      </div>

      {formData.voice_id && formData.voice_name && (
        <div className="flex items-center gap-2 p-2.5 bg-emerald-50 border border-emerald-200 rounded-lg">
          <Check className="h-4 w-4 text-emerald-600 flex-shrink-0" />
          <span className="text-sm text-emerald-800">
            Voce attuale: <strong>{formData.voice_name}</strong>
          </span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="ml-auto text-xs text-red-500 hover:text-red-700 hover:bg-red-50 h-7 px-2"
            onClick={() => setFormData(prev => ({ ...prev, voice_id: '', voice_name: '', voice_public_owner_id: '' }))}
          >
            Rimuovi
          </Button>
        </div>
      )}

      <div className="max-h-60 overflow-y-auto space-y-2 border rounded-lg p-2">
        {isLoadingVoices ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-emerald-500" />
          </div>
        ) : voicesError ? (
          <div className="flex flex-col items-center gap-2 py-6 px-4">
            <XCircle className="h-8 w-8 text-red-400" />
            <p className="text-center text-sm text-red-600 font-medium">{voicesError}</p>
            <Button variant="outline" size="sm" onClick={loadVoices} className="mt-1 text-xs">
              <RefreshCw className="h-3 w-3 mr-1" /> Riprova
            </Button>
          </div>
        ) : voices.length === 0 ? (
          <p className="text-center text-muted-foreground py-8 text-sm">Nessuna voce trovata</p>
        ) : (
          voices.map(voice => (
            <Card
              key={voice.voice_id}
              className={`cursor-pointer transition-all ${
                formData.voice_id === voice.voice_id
                  ? 'border-2 border-emerald-500 bg-emerald-50'
                  : 'hover:border-border'
              }`}
              onClick={() => setFormData(prev => ({ ...prev, voice_id: voice.voice_id, voice_name: voice.name, voice_public_owner_id: voice._source === 'library' ? (voice.public_owner_id || '') : '' }))}
            >
              <CardContent className="p-3 flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm">{voice.name}</span>
                    {voice._source === 'account' && (
                      <Badge variant="secondary" className="text-[10px] bg-emerald-100 text-emerald-700">Il mio piano</Badge>
                    )}
                    {voice.category && (
                      <Badge variant="secondary" className="text-[10px]">{voice.category}</Badge>
                    )}
                    {voice.language && (
                      <Badge variant="outline" className="text-[10px] border-blue-200 text-blue-600">{voice.language}</Badge>
                    )}
                    {voice.gender && (
                      <Badge variant="outline" className="text-[10px]">{voice.gender}</Badge>
                    )}
                  </div>
                  {voice.description && (
                    <p className="text-xs text-muted-foreground mt-0.5 truncate">{voice.description}</p>
                  )}
                  {!voice.description && voice.labels && (
                    <div className="flex gap-1 mt-1 flex-wrap">
                      {Object.entries(voice.labels).slice(0, 3).map(([k, v]) => (
                        <Badge key={k} variant="outline" className="text-[10px]">{v}</Badge>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {formData.voice_id === voice.voice_id && <Check className="h-4 w-4 text-emerald-500" />}
                  {voice.preview_url && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0"
                      onClick={(e) => { e.stopPropagation(); handlePlayVoice(voice); }}
                    >
                      {playingVoiceId === voice.voice_id ? (
                        <Pause className="h-4 w-4" />
                      ) : (
                        <Play className="h-4 w-4" />
                      )}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      <div className="space-y-3 pt-2">
        <div className="space-y-1">
          <Label className="font-medium text-sm">Modello TTS</Label>
          <select
            className="w-full rounded-md border border-border bg-card px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-inset focus:ring-purple-500"
            value={formData.tts_model}
            onChange={(e) => setFormData(prev => ({ ...prev, tts_model: e.target.value }))}
          >
            <option value="eleven_flash_v2_5">⚡ Flash v2.5 — Veloce + Qualità alta (Consigliato per real-time)</option>
            <option value="eleven_turbo_v2_5">🚀 Turbo v2.5 — Ultra-veloce, latenza minima</option>
            <option value="eleven_flash_v2">⚡ Flash v2 — Veloce, compatibilità ampia</option>
            <option value="eleven_multilingual_v2">🎵 Multilingual v2 — Massima qualità (più lento, +2-3s latenza)</option>
          </select>
        </div>
        <Label className="font-medium">Parametri Voce</Label>
        <div className="space-y-3">
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                <Label className="text-xs">Stabilità: {formData.voice_stability.toFixed(2)}</Label>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="h-3.5 w-3.5 text-muted-foreground hover:text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent side="right" align="center" className="max-w-xs text-xs leading-relaxed z-[9999]">
                      <p>{VOICE_PARAM_TOOLTIPS.stability}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <span className="text-[10px] text-muted-foreground">
                {formData.voice_stability < 0.3 ? 'Espressivo' : formData.voice_stability > 0.7 ? 'Stabile' : 'Bilanciato'}
              </span>
            </div>
            <input
              type="range"
              value={formData.voice_stability}
              onChange={(e) => setFormData(prev => ({ ...prev, voice_stability: parseFloat(e.target.value) }))}
              min={0}
              max={1}
              step={0.01}
              className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-emerald-500"
            />
          </div>
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                <Label className="text-xs">Somiglianza: {formData.voice_similarity.toFixed(2)}</Label>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="h-3.5 w-3.5 text-muted-foreground hover:text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent side="right" align="center" className="max-w-xs text-xs leading-relaxed z-[9999]">
                      <p>{VOICE_PARAM_TOOLTIPS.similarity}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <span className="text-[10px] text-muted-foreground">
                {formData.voice_similarity < 0.5 ? 'Libero' : formData.voice_similarity > 0.8 ? 'Alta fedeltà' : 'Bilanciato'}
              </span>
            </div>
            <input
              type="range"
              value={formData.voice_similarity}
              onChange={(e) => setFormData(prev => ({ ...prev, voice_similarity: parseFloat(e.target.value) }))}
              min={0}
              max={1}
              step={0.01}
              className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-emerald-500"
            />
          </div>
        </div>
      </div>

      <div className="pt-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setShowCloneSection(!showCloneSection)}
          className="w-full"
        >
          <Mic className="h-4 w-4 mr-2" />
          Clona Voce
        </Button>
        {showCloneSection && (
          <div className="mt-3 p-3 bg-muted rounded-lg border space-y-3">
            <div className="space-y-2">
              <Label className="text-xs">Nome Voce Clonata</Label>
              <Input
                value={cloneName}
                onChange={(e) => setCloneName(e.target.value)}
                placeholder="La mia voce"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs">File Audio</Label>
              <Input
                type="file"
                accept="audio/*"
                onChange={(e) => setCloneFile(e.target.files?.[0] || null)}
              />
              <p className="text-[10px] text-muted-foreground">MP3, WAV, M4A - Minimo 30 secondi di parlato chiaro</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-4">
      {!isEdit ? (
        <div className="text-center py-8 text-muted-foreground">
          <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
          <p>Salva prima l'agente per gestire la Knowledge Base</p>
        </div>
      ) : (
        <KnowledgeBaseManager agentId={agent.id} />
      )}
    </div>
  );

  const renderStep4 = () => (
    <div className="space-y-4">
      <div className="p-4 bg-muted rounded-lg border">
        <div className="flex items-start gap-3">
          <Wrench className="h-5 w-5 text-muted-foreground mt-0.5" />
          <div>
            <p className="text-sm font-medium">Tools & MCP</p>
            <p className="text-xs text-muted-foreground mt-1">
              I tools MCP vengono abbinati a questo agente dal pannello <strong>Agent Bindings</strong> nelle Impostazioni Sviluppatore.
              Dopo aver salvato l'agente, vai in Impostazioni Sviluppatore → Agent Bindings per configurare i tools.
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-2">
        <Label className="text-sm font-medium">Modalità Approvazione</Label>
        <Select
          value={formData.approval_mode}
          onValueChange={(value) => setFormData(prev => ({ ...prev, approval_mode: value }))}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="always">Sempre Chiedi</SelectItem>
            <SelectItem value="selective">Selettivo</SelectItem>
            <SelectItem value="none">Nessuna Approvazione</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );

  const renderStep5 = () => (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-2">
        <Zap className="h-4 w-4 text-amber-600" />
        <h4 className="text-sm font-medium">Pre-call Hooks</h4>
      </div>
      <p className="text-xs text-muted-foreground">
        Azioni eseguite automaticamente prima di ogni conversazione (inbound &amp; outbound) per arricchire il contesto dell'agente.
        I valori vengono iniettati nel prompt e come <code>dynamic_variables</code> ElevenLabs (es. <code>{`{{caller_number}}`}</code>, <code>{`{{rag_preload_context}}`}</code>).
      </p>
      <PreCallHooksSection
        hooks={formData.pre_call_hooks}
        onChange={(hooks) => setFormData(prev => ({ ...prev, pre_call_hooks: hooks }))}
        agentType="voice"
      />
    </div>
  );

  const renderStep6 = () => (
    <div className="space-y-4">
      <Card className="border">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-blue-50 rounded-lg">
              <Phone className="h-5 w-5 text-blue-500" />
            </div>
            <div>
              <p className="font-medium text-sm">Canali di Comunicazione</p>
              <p className="text-xs text-muted-foreground">Scegli su quali canali questo agente può ricevere chiamate</p>
            </div>
          </div>

          <div className="flex items-center justify-between p-3 bg-muted rounded-lg border border-border">
            <div>
              <p className="text-sm font-medium text-foreground">Canale SIP (Telefono)</p>
              <p className="text-xs text-muted-foreground">Abilita la ricezione di chiamate via SIP trunk</p>
            </div>
            <Switch
              checked={formData.channel_sip}
              onCheckedChange={(checked) => setFormData(prev => ({ ...prev, channel_sip: checked }))}
            />
          </div>

          <div className="flex items-center justify-between p-3 bg-muted rounded-lg border border-border">
            <div>
              <p className="text-sm font-medium text-foreground">Canale WebRTC (Widget Web)</p>
              <p className="text-xs text-muted-foreground">Abilita il widget embeddabile su siti web</p>
            </div>
            <Switch
              checked={formData.channel_webrtc}
              onCheckedChange={(checked) => setFormData(prev => ({ ...prev, channel_webrtc: checked }))}
            />
          </div>

          {formData.channel_sip && (
            <div className="p-3 bg-amber-50 rounded-lg border border-amber-300">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                <div className="space-y-1">
                  <p className="text-xs font-semibold text-amber-800">Configurazione SIP Trunk richiesta sulla Dashboard ElevenLabs</p>
                  <p className="text-xs text-amber-700">
                    Con il canale SIP attivo, questo agente potrà ricevere chiamate telefoniche. Tuttavia, il SIP Trunk va configurato <strong>manualmente</strong> sulla dashboard di ElevenLabs:
                  </p>
                  <ul className="text-xs text-amber-700 list-disc list-inside space-y-0.5 ml-1">
                    <li><strong>Inbound Trunk</strong>: aggiungi <code className="px-1 bg-white/60 rounded">{import.meta.env.VITE_SIP_PUBLIC_HOST || '85.94.215.109'}/32</code> come unico IP autorizzato (gateway centrale, lo stesso usato dagli altri provider)</li>
                    <li><strong>Outbound Trunk</strong>: indirizzo = <code className="px-1 bg-white/60 rounded">{import.meta.env.VITE_SIP_PUBLIC_HOST || '85.94.215.109'}</code>, transport TLS porta 5061</li>
                    <li><strong>Media Encryption</strong>: "allowed"</li>
                  </ul>
                  <p className="text-xs text-amber-600 italic mt-1">
                    Questa configurazione va fatta una sola volta per numero. Non modificare le rotte inbound dopo aver configurato il trunk, altrimenti potrebbe essere necessario riconfigurare.
                  </p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {isEdit && agent?.elevenlabs_agent_id && (
        <Card className="border">
          <CardContent className="p-4 space-y-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-50 rounded-lg">
                <Globe className="h-5 w-5 text-emerald-500" />
              </div>
              <div>
                <p className="font-medium text-sm">Widget WebRTC</p>
                <p className="text-xs text-muted-foreground">Widget embeddabile su qualsiasi sito web</p>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs">Codice Embed</Label>
              <Textarea
                readOnly
                rows={3}
                className="font-mono text-xs bg-muted"
                value={`<script src="https://elevenlabs.io/convai-widget/index.js" async type="text/javascript"></script>\n<elevenlabs-convai agent-id="${agent.elevenlabs_agent_id}"></elevenlabs-convai>`}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleCopyEmbed}
                className="w-full"
              >
                {copiedEmbed ? (
                  <><CheckCircle className="h-4 w-4 mr-1 text-emerald-500" /> Copiato!</>
                ) : (
                  <><Copy className="h-4 w-4 mr-1" /> Copia Codice</>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="border">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-purple-50 rounded-lg">
              <FileText className="h-5 w-5 text-purple-500" />
            </div>
            <div>
              <p className="font-medium text-sm">Trascrizione & Analisi</p>
              <p className="text-xs text-muted-foreground">Salva le conversazioni e analizza il sentiment</p>
            </div>
          </div>

          <div className="flex items-center justify-between p-3 bg-muted rounded-lg border border-border">
            <div>
              <p className="text-sm font-medium text-foreground">Trascrizione Conversazioni</p>
              <p className="text-xs text-muted-foreground">Salva il testo di ogni conversazione</p>
            </div>
            <Switch
              checked={formData.transcription_enabled}
              onCheckedChange={(checked) => setFormData(prev => ({
                ...prev,
                transcription_enabled: checked,
                sentiment_enabled: checked ? prev.sentiment_enabled : false,
              }))}
            />
          </div>

          <div className="flex items-center justify-between p-3 bg-muted rounded-lg border border-border">
            <div>
              <p className="text-sm font-medium text-foreground">Analisi Sentiment</p>
              <p className="text-xs text-muted-foreground">Analizza emozioni durante la conversazione</p>
            </div>
            <Switch
              checked={formData.sentiment_enabled}
              onCheckedChange={(checked) => setFormData(prev => ({ ...prev, sentiment_enabled: checked }))}
              disabled={!formData.transcription_enabled}
            />
          </div>

          {formData.sentiment_enabled && !formData.transcription_enabled && (
            <p className="text-xs text-amber-600">L'analisi sentiment richiede la trascrizione attiva</p>
          )}

          <div className="flex items-center justify-between p-3 bg-muted rounded-lg border border-border">
            <div>
              <p className="text-sm font-medium text-foreground">Memoria Contesto</p>
              <p className="text-xs text-muted-foreground">Ricorda informazioni durante la sessione corrente</p>
            </div>
            <Switch
              checked={formData.context_memory_enabled}
              onCheckedChange={(checked) => setFormData(prev => ({ ...prev, context_memory_enabled: checked }))}
            />
          </div>

          <div className="flex items-center justify-between p-3 bg-muted rounded-lg border border-border">
            <div>
              <p className="text-sm font-medium text-foreground">Memoria Semantica</p>
              <p className="text-xs text-muted-foreground">Ricorda fatti e preferenze tra sessioni diverse</p>
            </div>
            <Switch
              checked={formData.semantic_memory_enabled}
              onCheckedChange={(checked) => setFormData(prev => ({ ...prev, semantic_memory_enabled: checked }))}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 0: return renderStep0();
      case 1: return renderStep1();
      case 2: return renderStep2();
      case 3: return renderStep3();
      case 4: return renderStep4();
      case 5: return renderStep5();
      case 6: return renderStep6();
      default: return null;
    }
  };

  const sections: AgentSectionDef[] = STEPS.map((s, i) => ({
    id: `s${i}`,
    label: s.label,
    icon: <s.icon className="h-4 w-4" />,
  }));
  const activeSectionId = `s${currentStep}`;
  const handleSectionChange = (id: string) => {
    const idx = parseInt(id.replace('s', ''), 10);
    if (Number.isNaN(idx)) return;
    if (idx < currentStep || validateStep(currentStep)) {
      setCurrentStep(idx);
    }
  };

  return (
    <>
    <AgentConfigShell
      open={open && promptMode !== 'flowBuilder'}
      onOpenChange={onOpenChange}
      title={isEdit ? 'Configura Agente ElevenLabs' : 'Nuovo Agente ElevenLabs'}
      subtitle={isEdit ? "Modifica la configurazione dell'agente vocale" : 'Crea un nuovo agente vocale ElevenLabs'}
      icon={<Volume2 className="h-5 w-5" />}
      accentColor="emerald"
      sections={sections}
      activeSectionId={activeSectionId}
      onSectionChange={handleSectionChange}
      onSave={handleSave}
      footerRight={
        <>
          <Button type="button" variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            Annulla
          </Button>
          <Button type="button" size="sm" onClick={handleSave} disabled={isSaving} className="bg-emerald-500 hover:bg-emerald-600">
            {isSaving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Check className="h-4 w-4 mr-1" />}
            Salva Agente
          </Button>
        </>
      }
    >
      {renderCurrentStep()}
    </AgentConfigShell>

    <KbModelChangeGuardDialog state={kbGuard.state} primaryButtonClassName="bg-emerald-500 hover:bg-emerald-600" />

    <VoiceAgentFlowBuilder
      isOpen={promptMode === 'flowBuilder'}
      onClose={() => setPromptMode('builder')}
      agentType="elevenlabs"
      currentPrompt={formData.prompt}
      flowDefinition={formData.flow_definition || null}
      agentName={formData.name}
      agentId={agent?.id}
      onSave={(prompt, flowDef) => {
        setFormData(prev => ({ ...prev, prompt, flow_definition: flowDef }));
        setPromptMode('builder');
      }}
    />
    </>
  );
}